import { Button, Stack, ViewItemFieldWithChildren } from '@fukuri-admins/ui/common';
import { StripePaymentField } from './StripePaymentField';
import styles from './styles.module.scss';

type Props = {
  /**
   * カードブランド名（日本語） + 下4桁のフォーマット済み表示文字列（例:「マスターカード（‥‥1234）」）。
   * カード未登録（404）の場合は undefined。
   */
  cardLabel?: string;
  /** カード変更フォーム（Payment Element + 保存ボタン）を展開しているか */
  isFormExpanded: boolean;
  /**
   * エラー文言（SetupIntent 発行の失敗 / カード登録の失敗）。エラーがない場合は undefined。
   *
   * フォーム内（保存ボタン付近）に表示する。SetupIntent 発行に失敗した場合は client_secret が
   * 無いため保存ボタンは非活性であり、両者のエラーが同時に起こることはない。
   */
  errorMessage?: string;
  /** カード登録成功時の案内文言。未登録・登録前は undefined */
  successMessage?: string;
  /** 保存ボタンを非活性にするか（client_secret 未取得 / SetupIntent 発行中 / 送信中） */
  isSubmitDisabled: boolean;
  /**
   * 編集ボタン押下時のハンドラ（フォームの展開 / 格納をトグルする）
   *
   * Button は `as?: 'button' | 'a'` のジェネリックなため `React.ComponentProps<typeof Button>['onClick']`
   * だと anchor 側との union になり代入できない。ここでは as を渡さず button として使うため 'button' で受ける。
   */
  onToggleForm: React.ComponentProps<'button'>['onClick'];
  /** 保存ボタン押下時のハンドラ（stripe / elements は StripePaymentField 側で取得して渡される） */
  onSubmitCard: React.ComponentProps<typeof StripePaymentField>['onSubmit'];
};

/**
 * ご契約情報 お支払い方法 Presentation
 *
 * - 決済方法（固定表示: クレジットカード払い）とカード情報（ブランド + 下4桁）を表示する
 * - カード未登録時（cardLabel が undefined）はカード情報行を表示しない
 * - 編集ボタンでカード変更フォーム（StripePaymentField）を展開 / 格納する。
 *   展開中はラベルが「キャンセル」に変わる
 * - カード登録に成功したときは successMessage を表示する（フォームは閉じられる）
 */
export const ContractInfoPaymentMethodPresentation: React.FC<Props> = ({
  cardLabel,
  isFormExpanded,
  errorMessage,
  successMessage,
  isSubmitDisabled,
  onToggleForm,
  onSubmitCard,
}) => (
  <Stack direction='column' gap={24}>
    <h2 className={styles.title}>お支払い方法</h2>
    <ViewItemFieldWithChildren label='決済方法' direction='row'>
      <Stack direction='column' gap={8}>
        <span>クレジットカード払い</span>
        <Stack direction='row' gap={24} alignItems='center'>
          {cardLabel && <span>{cardLabel}</span>}
          <Button variant='secondary' size='sm' onClick={onToggleForm}>
            {isFormExpanded ? 'キャンセル' : '編集'}
          </Button>
        </Stack>
      </Stack>
    </ViewItemFieldWithChildren>
    {successMessage && <p className={styles.successMessage}>{successMessage}</p>}
    {isFormExpanded && (
      <StripePaymentField
        isSubmitDisabled={isSubmitDisabled}
        errorMessage={errorMessage}
        onSubmit={onSubmitCard}
      />
    )}
  </Stack>
);
